import { useState, useEffect, useRef, useCallback } from 'react';
import { isApple, watchApplePurchases, manageApple, openWebBilling } from '../applePurchases';
import { auth, db, functions } from '../firebase';
import { onAuthStateChanged, sendEmailVerification } from 'firebase/auth';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { deleteTapTapAccount } from '../deleteAccount';

const useAuthSync = ({ defaultSettings, setCurrentPage, onLoadStats, onBeginStatsLoad, onLogoutRef }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettingsState] = useState(defaultSettings);
  const settingsRef = useRef(defaultSettings);
  const [isPro, setIsPro] = useState(false);
  const [hasBillingAccount, setHasBillingAccount] = useState(false);
  const [entitlementReady, setEntitlementReady] = useState(false);
  const billingProviderRef = useRef(null);
  const settingsOwnerRef = useRef(null);
  const settingsReadyRef = useRef(false);
  const pendingSettingsRef = useRef({});
  const queuedWriteRef = useRef(null);
  const saveTimerRef = useRef(null);
  const setSettings = useCallback((value) => {
    const next = typeof value === 'function' ? value(settingsRef.current) : value;
    settingsRef.current = next;
    setSettingsState(next);
  }, []);

  const flushSettings = useCallback(async () => {
    clearTimeout(saveTimerRef.current);
    const queued = queuedWriteRef.current;
    queuedWriteRef.current = null;
    if (!queued || auth.currentUser?.uid !== queued.uid) return;
    try {
      await setDoc(doc(db, 'users', queued.uid), { settings: queued.settings }, { merge: true });
    } catch (error) {
      console.warn('Could not persist settings:', error?.code || 'unavailable');
    }
  }, []);

  const queueSettings = useCallback((uid, value) => {
    queuedWriteRef.current = { uid, settings: value };
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(flushSettings, 400);
  }, [flushSettings]);

  useEffect(() => {
    const failsafe = setTimeout(() => setLoading(false), 5000);
    let generation = 0;
    let stopEntitlement = () => {};
    let stopApple = () => {};
    let expiryTimer;
    let updateAccess = () => {};
    let retryHydration = () => {};
    const onOnline = () => retryHydration();
    const onVisible = () => {
      if (document.visibilityState !== 'hidden') { updateAccess(); retryHydration(); }
    };
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      const currentGeneration = ++generation;
      stopEntitlement();
      stopApple();
      stopApple = () => {};
      billingProviderRef.current = null;
      clearTimeout(expiryTimer);
      clearTimeout(saveTimerRef.current);
      queuedWriteRef.current = null;
      pendingSettingsRef.current = {};
      settingsReadyRef.current = false;
      settingsOwnerRef.current = firebaseUser?.uid || null;
      updateAccess = () => {};
      retryHydration = () => {};
      stopEntitlement = () => {};
      setIsPro(false);
      setHasBillingAccount(false);
      setEntitlementReady(false);
      if (firebaseUser) {
        setUser(firebaseUser);
        if (isApple) stopApple = watchApplePurchases(firebaseUser.uid);
        setSettings(defaultSettings);
        onBeginStatsLoad?.(firebaseUser.uid);
        setCurrentPage('intro');
        try {
          stopEntitlement = onSnapshot(doc(db, 'entitlements', firebaseUser.uid), snapshot => {
            if (currentGeneration !== generation) return;
            const data = snapshot.exists() ? snapshot.data() : null;
            updateAccess = () => {
              if (currentGeneration !== generation) return;
              clearTimeout(expiryTimer);
              const now = Date.now();
              const trialEnd = data?.trialEndsAt?.toMillis?.() || 0;
              const apples = Object.values(data?.appleSubscriptions || {});
              const appleEnd = Math.max(0, ...apples.filter(item => [1, 4].includes(item.status)).map(item => item.expiresAt));
              const stripePaid = ['active', 'trialing'].includes(data?.stripeSubscriptionStatus);
              // Legacy entitlements remain supported until the coordinated backend deploy.
              const legacyPaid = data?.pro === true && !['trial', 'apple', 'none'].includes(data?.source);
              setIsPro(stripePaid || appleEnd > now || (data?.pro === true && trialEnd > now) || legacyPaid);
              billingProviderRef.current = appleEnd > now || (apples.length && !data?.stripeCustomerId) ? 'apple' : 'stripe';
              setHasBillingAccount(!!data?.stripeCustomerId || apples.length > 0);
              setEntitlementReady(true);
              const nextExpiry = Math.min(...[trialEnd, appleEnd].filter(end => end > now));
              if (Number.isFinite(nextExpiry)) {
                expiryTimer = setTimeout(updateAccess, Math.min(nextExpiry - now + 1, 2147483647));
              }
            };
            updateAccess();
          }, error => {
            if (currentGeneration !== generation) return;
            clearTimeout(expiryTimer);
            updateAccess = () => {};
            console.warn('Pro access sync unavailable, using Free access:', error?.message);
            setIsPro(false);
            setEntitlementReady(true);
          });
        } catch (error) {
          console.warn('Pro access sync unavailable, using Free access:', error?.message);
          setIsPro(false);
          setEntitlementReady(true);
        }
        let hydrating = false;
        const hydrate = async () => {
          if (hydrating || settingsReadyRef.current || currentGeneration !== generation) return;
          hydrating = true;
          try {
            const reference = doc(db, 'users', firebaseUser.uid);
            const snapshot = await getDoc(reference);
            if (currentGeneration !== generation) return;
            const data = snapshot.exists() ? snapshot.data() : {};
            let restored = { ...defaultSettings, ...(data.settings || {}), orbColor: 'blue', kidsMode: false, ...pendingSettingsRef.current };
            setSettings(restored);
            // Seed settings before stats writes: rules require the settings map.
            if (!snapshot.exists()) {
              await setDoc(reference, { settings: restored, createdAt: new Date().toISOString() }, { merge: true });
              if (currentGeneration !== generation) return;
            }
            // A first-account seed can itself be slow; include changes made
            // while that write was in flight before clearing the pending patch.
            const pending = pendingSettingsRef.current;
            restored = { ...restored, ...pending };
            setSettings(restored);
            settingsReadyRef.current = true;
            onLoadStats(data.stats, firebaseUser.uid);
            if (Object.keys(pending).length) queueSettings(firebaseUser.uid, restored);
            pendingSettingsRef.current = {};
          } catch (error) {
            // Keep local controls usable, but do not replace remote state with
            // defaults when its initial read has failed.
            console.warn('Settings sync unavailable, using defaults:', error?.message);
          } finally { hydrating = false; }
        };
        retryHydration = hydrate;
        hydrate();
      } else {
        onLogoutRef.current?.();
        setSettings(defaultSettings);
        setEntitlementReady(true);
        setUser(null);
        setCurrentPage('onboarding');
      }
      clearTimeout(failsafe);
      setLoading(false);
    }, error => {
      console.error('Auth state error:', error?.code || 'unavailable');
      clearTimeout(failsafe);
      setLoading(false);
    });
    return () => {
      generation += 1;
      stopEntitlement();
      stopApple();
      clearTimeout(failsafe);
      clearTimeout(expiryTimer);
      clearTimeout(saveTimerRef.current);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
      unsubscribe();
    };
    // Auth lifetime callbacks use refs/current-generation checks; subscriptions
    // must not restart when settings or navigation change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onHide = () => { if (document.visibilityState === 'hidden') flushSettings(); };
    window.addEventListener('pagehide', flushSettings);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      window.removeEventListener('pagehide', flushSettings);
      document.removeEventListener('visibilitychange', onHide);
    };
  }, [flushSettings]);

  const saveSettings = useCallback((patch) => {
    const next = { ...settingsRef.current, ...patch };
    setSettings(next);
    const uid = auth.currentUser?.uid;
    if (!uid || uid !== settingsOwnerRef.current) return;
    if (!settingsReadyRef.current) pendingSettingsRef.current = { ...pendingSettingsRef.current, ...patch };
    else queueSettings(uid, next);
  }, [setSettings, queueSettings]);

  const deleteAccount = useCallback(async (password) => {
    // Stop delayed settings writes before the server marks deletion in progress.
    clearTimeout(saveTimerRef.current);
    queuedWriteRef.current = null;
    await deleteTapTapAccount(password);
  }, []);

  const manageSubscription = useCallback(async () => {
    if (billingProviderRef.current === 'apple') {
      if (isApple) return manageApple();
      window.location.href = 'https://apps.apple.com/account/subscriptions';
      return;
    }
    const portal = httpsCallable(functions, 'createPortalSession');
    const result = isApple ? await portal({ cancelOnly: true }) : await portal();
    if (isApple) return openWebBilling(result.data.url);
    window.location.href = result.data.url;
  }, []);
  const resendVerificationEmail = useCallback(async () => {
    if (auth.currentUser) await sendEmailVerification(auth.currentUser);
  }, []);

  return {
    user, setUser, loading, settings, setSettings, settingsRef, saveSettings, flushSettings,
    deleteAccount, manageSubscription, resendVerificationEmail, isPro, hasBillingAccount, entitlementReady,
  };
};
export default useAuthSync;
